import React, {useEffect, useRef, useState} from 'react';
import {useQueryClient, useQuery} from 'react-query';
import {Formik} from 'formik';
import {Autocomplete, Box, Button, Chip, Dialog, DialogTitle, Divider, Grid, Stack, TextField,
  Typography} from '@mui/material';
import DataStreamCron from '../data-stream-cron/DataStreamCron';
import DataStreamDropDown from '../data-stream-dropdown/DataStreamDropdown';
import PageHooks from '../hooks';
import DialogHooks from './hooks';
import PageHandlers from '../handlers';
import DialogHandlers from './handlers';
import OwnerAssociationService from '../../../../apis/OwnerAssociationService';
import PartitionConfigsEditor from './PartitionConfigsEditor';
import Helpers from './helpers';
import IyoFormSection from '../../../common/IyoFormSection';

// Writes the worked-out name into the form whenever the selections behind it change. It
// lives in its own component because the fields are rendered inside Formik's render prop,
// where a hook cannot be called.
const StreamNameSync = ({streamName, values, setValues}) => {
  // The description this component last filled in. While the field still holds it, nobody
  // has written one of their own, so it keeps following the name.
  const defaulted = useRef('');

  // Both fields go out in one setValues. A setFieldValue revalidates against the values as
  // they stood at the last render, so writing the name and then the description as two calls
  // would have the second validate a form whose name had gone back to the old one - and the
  // last result to land is the one Save reads.
  useEffect(() => {
    const next = {};

    // Nothing is written until there is a name to write, so a stream being edited keeps its
    // stored name while the owner, provider and device lists are still loading - or if the
    // device behind a PC Agent stream has since been deleted, leaving nothing to rebuild from.
    if (streamName && values.name !== streamName) next.name = streamName;

    // The description starts from the name and is then left alone from the moment somebody
    // types their own - including a description that was already there when the stream was
    // opened. It is a default, not a rule: only a PC Agent *provider* has to describe itself
    // by its name.
    const name = next.name ?? values.name;
    const stillDefault = values.description === '' || values.description === defaulted.current;
    if (name && values.description !== name && stillDefault) {
      next.description = name;
      defaulted.current = name;
    }

    if (!Object.keys(next).length) return;

    setValues({...values, ...next});
  }, [streamName, values, setValues]);

  return null;
};

// The name is not typed - it is what the pickers beside it add up to. It gets the accent
// treatment so it reads as the result of the form rather than another empty field somebody
// has forgotten to fill in. The description sits inside it because it starts life as a copy
// of the name.
const StreamIdentityCard = ({name, placeholder, hint, error, children}) => {
  // An empty card is not a fault the reader can act on - it is waiting on a selection, which
  // the placeholder already says. Only report a name that is actually wrong.
  const problem = name ? error : '';

  return (
    <IyoFormSection title='Will be saved as' accent error={Boolean(problem)}>
      <Typography
        variant='subtitle1'
        component='div'
        color={name ? 'text.primary' : 'text.disabled'}
        sx={{fontWeight: name ? 500 : 400, wordBreak: 'break-word', minHeight: '1.5rem'}}
      >
        {name || placeholder}
      </Typography>
      <Typography
        variant='caption'
        component='div'
        color={problem ? 'error' : 'text.secondary'}
        sx={{mb: 2}}
      >
        {problem || hint}
      </Typography>
      {children}
    </IyoFormSection>
  );
};

const DataStreamEditDialog = ({state, setState}) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const refetch = async () => {
      await queryClient.refetchQueries("getProperties");
    };
    refetch();
  }, [state.providerId, queryClient]);

  const { data: childOwners = [] } = useQuery(
    ['childOwners', state.ownerId],
    async () => {
      if (!state.ownerId) return [];
      return await OwnerAssociationService.getOneByParentId(state.ownerId);
    },
    {
      enabled: Boolean(state.ownerId),
    }
  );

  const postDataStream = PageHooks.useDataStreamQuery('POST', queryClient, state, setState);
  const putDataStream = PageHooks.useDataStreamQuery('PUT', queryClient, state, setState);

  const getConsumers = DialogHooks.useConsumerQuery('GET', setState);
  const getProviders = DialogHooks.useProviderQuery('GET', setState);
  const getOwners = DialogHooks.useOwnerQuery('GET', setState);
  const getDevices = DialogHooks.useDeviceQuery('GET', setState);
  const getProperties = DialogHooks.usePropertiesQuery(state, setState, getProviders);
  const selectedProvider = getProviders.data?.find(p => p.id === state.providerId);
  const selectedOwner = getOwners.data?.find(o => o.id === state.ownerId);
  const providerScheme = selectedProvider?.partition_scheme || null;

  // A PC Agent feed arrives from one machine at the owner's site, so the stream has to name
  // the device it comes from - and is named after it. No other kind of provider has a device.
  const isDeviceStream = Helpers.isDeviceProvider(selectedProvider);
  const ownerDevices = Helpers.devicesForOwner(getDevices.data, selectedOwner);
  const selectedDevice = ownerDevices.find(d => d.id === state.deviceId);

  // The rest are numbered instead, against the list the grid already holds, so the number
  // handed out accounts for what is out there rather than starting from one every time.
  const getDataStreams = PageHooks.useDataStreamQuery('GET', queryClient, state, setState);

  // Every stream is named from its selections, so a name says what the stream is and two
  // people setting up the same feed arrive at the same one. Opening a stream and saving it
  // must not rename it though - the name is unique, the scheduler is keyed off it, and it is
  // what people search for - so it is rebuilt only once one of its parts actually changes.
  const nameSourcesUnchanged = state.editMode === 'edit' &&
    Boolean(state.rowEditing?.name) &&
    state.ownerId === state.rowEditing.owner_id &&
    state.providerId === state.rowEditing.provider_id &&
    (state.deviceId ?? null) === (state.rowEditing.device_id ?? null);

  const derivedName = nameSourcesUnchanged
    ? state.rowEditing.name
    : Helpers.streamName({
      provider: selectedProvider,
      owner: selectedOwner,
      device: selectedDevice,
      dataStreams: getDataStreams.data,
      rowEditing: state.editMode === 'edit' ? state.rowEditing : null,
    });

  const getInitialValues = DialogHooks.useInitialValuesQuery(state, setState, getProperties, providerScheme);

  const resetHWM = (values) => {
    values.last_retrieval_date = '';
    setState.setLastRetrievalDate('');
  }

  const anyOwner = Helpers.routesToAnyOwner(state.ownerId);

  const showPartitionConfigs = () => {
    const provider = selectedProvider;
    const owner = selectedOwner;

    // Existing rules are always shown: the pipeline applies them regardless of the
    // provider flag, so hiding them would leave live config unreachable.
    if (state.rowEditing?.partition_configs) return true;

    // iYotah streams always need rules, and have neither children nor multi_site.
    if (anyOwner) return true;

    if (!Boolean(provider?.multi_tenant_enabled)) return false;

    return Boolean(owner?.multi_site) && childOwners.length > 0;
  };

  return (
    <Dialog
      open={state.editOpen}
      onClose={() => DialogHandlers.handleClose(setState)}
      maxWidth='md'
      fullWidth={true}
    >
      <DialogTitle id='alert-dialog-title'>
        {
          state.editMode === 'edit' ? `Edit Data Stream ${state.rowEditing.id}` :
            state.editMode === 'create' ? 'Create Data Stream' : ''
        }
      </DialogTitle>
      {
        !getInitialValues.data ?
          null :
          <Formik
            initialValues={getInitialValues.data}

            validate={values => DialogHandlers.validate(values, getProperties, derivedName)}

            onSubmit={state.editMode === 'edit' ? (values) => PageHandlers.handleEditSubmit(values,
              queryClient, state, setState, putDataStream, getProperties.data) :
              state.editMode === 'create' ? (values) => PageHandlers.handleCreateSubmit(values, state,
                setState, state.dataStreamFields, postDataStream, getProperties.data) :
                () => {}}
          >
            {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                setFieldValue,
                setValues,
                resetForm,
                touched,
                values
              }) => (
              <form onSubmit={handleSubmit}>
                {/* Keyed so each time the dialog opens on a stream it starts over: the
                    component remembers the description it filled in, and that memory must
                    not carry across to the next stream. */}
                <StreamNameSync
                  key={`${state.editMode}-${state.rowEditing?.id ?? 'new'}`}
                  streamName={derivedName}
                  values={values}
                  setValues={setValues}
                />
                <div style={{margin: '2%'}}>
                  <Grid container spacing={2}>
                    {/* ##########################[ Source ]##############################
                        The pickers on the left, what they name on the right. The name is
                        worked out from these three fields rather than typed, so it is read
                        beside them - above, it looked like a field that would not fill in. */}
                    <Grid item xs={6}>
                      <IyoFormSection title='Source'>
                        <Stack spacing={2}>
                          <DataStreamDropDown
                            fieldName='owner'
                            dataList={getOwners.data}
                            value={state.ownerId || ''}
                            handleChange={(selection) =>
                              DialogHandlers.handleOwnerSelect(selection, setState, setFieldValue)}
                            isMultiple={false}
                          />
                          <DataStreamDropDown
                            fieldName='provider'
                            dataList={getProviders.data}
                            value={state.providerId || ''}
                            handleChange={(selection) => DialogHandlers.handleProviderSelect(selection, values,
                              resetForm, setState, getProperties,
                              getProviders.data?.find(p => p.id === selection.value) || null)}
                            isMultiple={false}
                          />
                          {/* Only a PC Agent provider gets a device: its rows are pushed by an
                              agent on one machine, and the stream records which. Offered from
                              the selected owner's devices, since the two share an owner. */}
                          {isDeviceStream && (
                            <Box>
                              <DataStreamDropDown
                                fieldName='device'
                                dataList={Helpers.deviceOptions(ownerDevices)}
                                value={state.deviceId || ''}
                                handleChange={(selection) => DialogHandlers.handleDeviceSelect(selection, setState)}
                                isMultiple={false}
                              />
                              {/* An empty picker otherwise reads as a page that failed to load. */}
                              {getDevices.data && state.ownerId && !ownerDevices.length && (
                                <Typography variant='caption' color='error'>
                                  This owner has no devices. Create one on the Devices page first.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Stack>
                      </IyoFormSection>
                    </Grid>
                    <Grid item xs={6}>
                      <StreamIdentityCard
                        name={values.name}
                        placeholder={isDeviceStream
                          ? 'Set once the owner, provider and device are chosen'
                          : 'Set once the owner and provider are chosen'}
                        hint={isDeviceStream
                          ? 'Device, owner and provider - the name PC Agent uses.'
                          : 'Provider, owner and the number of this stream for that pair.'}
                        error={errors.name}
                      >
                        {/* Starts from the name and is then whatever whoever set the stream up
                            wants to say. Unlike the provider's own description, which a PC
                            Agent provider keeps equal to its name, this is only a default.
                            The paper background goes on the input itself, not the field: on
                            the field it painted the helper text underneath white too, which
                            left a slab of the wrong colour sitting on the card's tint. */}
                        <TextField
                          name='description'
                          label='Description'
                          value={values.description}
                          error={Boolean(touched.description && errors.description)}
                          helperText={(touched.description && errors.description) ||
                            'Defaults to the name. Edit it for anything more useful.'}
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          variant='outlined'
                          InputProps={{sx: {bgcolor: 'background.paper'}}}
                        />
                      </StreamIdentityCard>
                    </Grid>
                    {/* ##########################[ Provider Settings ]##############################
                        Driven by the provider's own JSON schema, so the section is only there
                        when the chosen provider declares something to fill in. */}
                    {Boolean(getProperties.data?.length) && (
                      <Grid item xs={12}>
                        <IyoFormSection
                          title='Provider settings'
                          description={`How this stream reaches ${selectedProvider?.name || 'the provider'}.`}
                        >
                          <Grid container spacing={2}>
                            {getProperties.data.map(field => (
                              <Grid item xs={6} key={field.name}>
                                {/* Entered one value at a time: site names contain commas,
                                    so no delimiter is unambiguous. */}
                                {field.type === 'array' ? (
                                  <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={Array.isArray(values[field.name]) ? values[field.name] : []}
                                    onChange={(event, next) => setFieldValue(field.name, next)}
                                    renderTags={(tags, getTagProps) =>
                                      tags.map((tag, index) => (
                                        <Chip size='small' label={tag} {...getTagProps({index})}/>
                                      ))
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={field.name}
                                        error={Boolean(touched[field.name] && errors[field.name])}
                                        helperText={(touched[field.name] && errors[field.name])
                                          || field.description}
                                        placeholder='Type a value and press Enter'
                                        variant='outlined'
                                      />
                                    )}
                                  />
                                ) : (
                                  <TextField
                                    name={field.name}
                                    label={field.name}
                                    value={values[field.name]}
                                    error={Boolean(touched[field.name] && errors[field.name])}
                                    helperText={(touched[field.name] && errors[field.name])
                                      || field.description}
                                    fullWidth
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant='outlined'
                                  />
                                )}
                              </Grid>
                            ))}
                          </Grid>
                        </IyoFormSection>
                      </Grid>
                    )}
                    {/* ##########################[ Row Routing ]############################## */}
                    {showPartitionConfigs() && (
                      <Grid item xs={12}>
                        <IyoFormSection
                          title='Row routing'
                          description='Which rows in this stream belong to which owner.'
                        >
                          <PartitionConfigsEditor
                            value={values.partition_configs}
                            onChange={(next) => setFieldValue('partition_configs', next)}
                            childOwners={childOwners}
                            owners={getOwners.data || []}
                            anyOwner={anyOwner}
                            providerScheme={providerScheme}
                            error={Boolean(touched.partition_configs && errors.partition_configs)}
                            helperText={(touched.partition_configs && errors.partition_configs) || ''}
                          />
                        </IyoFormSection>
                      </Grid>
                    )}
                    {/* ##########################[ Delivery ]############################## */}
                    <Grid item xs={12}>
                      <IyoFormSection
                        title='Delivery'
                        description='Where the extracted data is sent once it lands.'
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <DataStreamDropDown
                              fieldName='consumers'
                              dataList={getConsumers.data}
                              value={state.consumerId || []}
                              handleChange={(selection) =>
                                DialogHandlers.handleConsumerSelect(selection, state, setState)}
                              isMultiple={true}
                            />
                          </Grid>
                        </Grid>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Schedule ]############################## */}
                    <Grid item xs={12}>
                      <IyoFormSection
                        title='Schedule'
                        description='When the stream runs, and how far through the source it has read.'
                      >
                        <Stack spacing={2}>
                          <Stack direction='row' spacing={2} alignItems='flex-start'>
                            <TextField
                              name='last_retrieval_date'
                              label='Last Retrieval Date'
                              value={values.last_retrieval_date}
                              error={Boolean(touched.last_retrieval_date && errors.last_retrieval_date)}
                              helperText={touched.last_retrieval_date && errors.last_retrieval_date}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              variant='outlined'
                              inputProps={{readOnly: true}}
                            />
                            <Button
                              color='primary'
                              onClick={() => resetHWM(values)}
                              size='large'
                              type='button'
                              variant='outlined'
                            >
                              Reset
                            </Button>
                          </Stack>
                          <DataStreamCron
                            fieldName='cron'
                            handleChange={(value) => DialogHandlers.handleCronUpdate(value, setState)}
                            cronValue={state.cron}
                            errors={errors}
                          />
                        </Stack>
                      </IyoFormSection>
                    </Grid>
                    {/* ##########################[ Submit/Cancel Row ]############################## */}
                    <Grid item xs={12}>
                      <Divider sx={{mb: 2}}/>
                      <Button
                        color='primary'
                        disabled={isSubmitting}
                        onClick={() => DialogHandlers.handleClose(setState)}
                        size='large'
                        type='button'
                        variant='outlined'
                        sx={{mr: 1}}
                      >
                        Cancel
                      </Button>
                      <Button
                        color='primary'
                        disabled={isSubmitting || !state.ownerId || !state.providerId ||
                          !state.consumerId || (!state.consumerId.length) ||
                          (isDeviceStream && !state.deviceId) ||
                          Object.keys(errors).length !== 0 || !values.name}
                        size='large'
                        type='submit'
                        variant='contained'
                      >
                        Save
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </form>
            )}
          </Formik>
      }
    </Dialog>
  );
};

export default DataStreamEditDialog;